import {format, utcToZonedTime} from 'date-fns-tz';
import {AchRejectCodes} from '../../../../constants';
import {Reject} from '../../../express/private/types/Rejects';
import {formatCurrency} from '../../../express/private/utils/format';
import {ReportExportColumn} from '../ReportExportColumn';

export const formatterNumber = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export type RejectColumnNamesType =
  | 'group'
  | 'agent'
  | 'mid'
  | 'dba'
  | 'rejectDate'
  | 'amountApplied' // originalRejectAmount
  | 'rejectCode'
  | 'action'
  | 'rejectFeeApplied' // manually updated reject fee
  | 'totalAmountDue' // Reject Amount + Reject Fee without any admin fees
  | 'totalAdminFee' // Manually entered amount by Finance or EPR user
  | 'amountOfPaymentApplied' // Manually entered
  | 'totalAmountDueByMid'
  | 'collectionStatus'
  | 'reportDate' // from ach file
  | 'midBoardedDate'
  | 'accountClosedDate'
  | 'lastBatchDate'
  | 'lastBatchAmount'
  | 'ticketNumber'
  | 'divertAmount'
  | 'totalAmountOwed' // Reject Amount + Reject Fee + Amount of Payment Applied + Full Divert/Credit Divert
  | 'collectionMethod'
  | 'notes';

export const AchRejectColumnsConfigRecord: Record<
  RejectColumnNamesType,
  ReportExportColumn<Reject>
> = {
  group: {
    id: 'group',
    label: 'Group',
    export: true,
  },
  agent: {
    id: 'agent',
    label: 'Agent',
    export: true,
  },
  mid: {
    id: 'mid',
    label: 'MID',
    export: true,
  },
  dba: {
    id: 'dba',
    label: 'DBA',
    export: true,
  },
  rejectDate: {
    id: 'rejectDate',
    label: 'ACH Reject Date',
    export: row => format(utcToZonedTime(new Date(row.rejectDate), 'UTC'), 'MMMM dd, yyyy'),
  },
  amountApplied: {
    id: 'amountApplied',
    label: 'Amount',
    export: row => formatCurrency(Number(row.amountApplied)),
  },
  rejectCode: {
    id: 'rejectCode',
    label: 'ACH Reject Code',
    export: row => {
      const reject = AchRejectCodes.find(x => x.code === row.rejectCode);
      return reject ? `${reject.code}: ${reject.description}` : '';
    },
  },
  action: {
    id: 'action',
    label: 'Action Required',
    export: true,
  },
  rejectFeeApplied: {
    id: 'rejectFeeApplied',
    label: 'ACH Reject Fee Amount',
    export: row => formatCurrency(Number(row.rejectFeeApplied)),
  },
  totalAmountDue: {
    id: 'totalAmountDue',
    label: 'Total Amount Due',
    export: row => formatCurrency(Number(row.totalAmountDue)),
  },
  totalAdminFee: {
    id: 'totalAdminFee',
    label: 'Total Admin Fee',
    export: row => formatCurrency(Number(row.totalAdminFee)),
  },
  totalAmountDueByMid: {
    id: 'totalAmountDueByMid',
    label: 'Total By MID',
    export: row => formatCurrency(Number(row.totalAmountDueByMid)),
  },
  amountOfPaymentApplied: {
    id: 'amountOfPaymentApplied',
    label: 'Amount Of Payment Applied',
    export: row => formatCurrency(Number(row.amountOfPaymentApplied)),
  },
  collectionStatus: {
    id: 'collectionStatus',
    label: 'Collection Status',
    export: row => row.collectionStatus ?? '',
  },
  reportDate: {
    id: 'reportDate',
    label: 'ACH Report Date',
    export: row => format(utcToZonedTime(new Date(row.reportDate), 'UTC'), 'MMMM dd, yyyy'),
  },
  midBoardedDate: {
    id: 'midBoardedDate',
    label: 'Date Boarded',
    export: row =>
      row.midBoardedDate
        ? format(utcToZonedTime(new Date(row.midBoardedDate), 'UTC'), 'MMMM dd, yyyy')
        : '',
  },
  accountClosedDate: {
    id: 'accountClosedDate',
    label: 'Date Account Closed',
    export: row =>
      row.accountClosedDate
        ? format(utcToZonedTime(new Date(row.accountClosedDate), 'UTC'), 'MMMM dd, yyyy')
        : '',
  },
  lastBatchDate: {
    id: 'lastBatchDate',
    label: 'Last Batch Date',
    export: row =>
      row.lastBatchDate
        ? format(utcToZonedTime(new Date(row.lastBatchDate), 'UTC'), 'MMMM dd, yyyy')
        : '',
  },
  lastBatchAmount: {
    id: 'lastBatchAmount',
    label: 'Last Batch Amount',
    export: row => formatCurrency(Number(row.lastBatchAmount)),
  },
  ticketNumber: {
    id: 'ticketNumber',
    label: 'Ticket Number',
    export: row => row.ticketNumber ?? '',
  },
  divertAmount: {
    id: 'divertAmount',
    label: 'Full Divert/Credit Divert',
    export: row => formatCurrency(Number(row.divertAmount)),
  },
  totalAmountOwed: {
    id: 'totalAmountOwed',
    label: 'Total Amount Owed',
    export: row => formatCurrency(Number(row.totalAmountOwed)),
  },
  collectionMethod: {
    id: 'collectionMethod',
    label: 'Collection Method',
    export: row => row.collectionMethod ?? '',
  },
  notes: {
    id: 'notes',
    label: 'Notes',
    export: row => row.notes ?? '',
  },
};
